import * as React from 'react';
import { AlertTriangle, LogIn, RefreshCcw, WifiOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Eyebrow } from './eyebrow';

// ─────────────────────────────────────────────────────────────────────────
// ServiceErrorState — calm, customer-safe error surface.
//
// Replaces the pattern of interpolating raw API errors into customer-facing
// markup. Maps known error shapes to friendly copy + a recovery action.
// Operator-console register: chrome-only, no Caveat, no character poses.
//
// Why this exists: the audit at docs/work/2026-05-07-ux-ui-audit/ found that
// every Convex-driven route (core, library, settings, business-xray) was
// exposing raw JWT payloads to buyers when auth failed. This primitive is
// the cross-surface fix.
// ─────────────────────────────────────────────────────────────────────────

export type ServiceErrorIntent = 'load' | 'submit' | 'navigate';
export type ServiceErrorTone = 'auth' | 'network' | 'server' | 'unknown';

export interface ServiceErrorStateProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /** The error from the failed call. Pass the raw error or message string — never interpolate it elsewhere. */
  error: unknown;
  /**
   * What the Builder was trying to do. Drives the body copy.
   *  - `load`: reading data (most common)
   *  - `submit`: posting an action
   *  - `navigate`: route lookup / redirect
   */
  intent?: ServiceErrorIntent;
  /** Short surface name for the eyebrow (e.g. "Settings", "Library", "Account"). Defaults to "Something" if omitted. */
  surface?: string;
  /** Refresh / retry callback. Renders the primary "Try again" button when present. */
  onRetry?: () => void;
  /** Sign-in callback. When the error classifies as `auth`, this is preferred over onRetry. */
  onSignIn?: () => void;
  /** Override the classifier (rare — for surfaces that know better than the heuristic). */
  toneOverride?: ServiceErrorTone;
  /** Compact variant — narrower padding, single-line body. Used inside form panels. */
  compact?: boolean;
}

/**
 * Classify an unknown error into one of four tones the UI knows how to render.
 * Never returns the raw error string — that's the whole point.
 */
export function classifyServiceError(error: unknown): ServiceErrorTone {
  if (error == null) return 'unknown';

  const message = errorToMessage(error).toLowerCase();

  // Auth: JWT, unauthorized, sign-in expired, session-verify failures.
  // These substrings are matched against the lowercased error.message text
  // thrown by requireConvexAuthToken (src/lib/appAuth.ts) and Convex's
  // own auth-failure responses. If you change the wording of the auth
  // error in appAuth.ts, update this matcher OR keep one of these
  // substrings present so the surface still gets the auth tone (which
  // shows "Sign back in to continue." instead of the generic "couldn't
  // load" message).
  if (
    message.includes('invalidauthheader') ||
    message.includes('could not parse jwt') ||
    message.includes('jwt') ||
    message.includes('unauthorized') ||
    message.includes('401') ||
    message.includes('sign in') ||
    message.includes('sign-in') ||
    message.includes('sign back in') ||
    message.includes('not authenticated') ||
    message.includes('auth') ||
    message.includes('verify your lab session') ||
    message.includes("couldn't verify your lab session") ||
    message.includes('lab session')
  ) {
    return 'auth';
  }

  // Network: fetch failed, offline, connection refused
  if (
    message.includes('fetch failed') ||
    message.includes('network') ||
    message.includes('econn') ||
    message.includes('offline') ||
    message.includes('failed to fetch') ||
    message.includes('aborted')
  ) {
    return 'network';
  }

  // Server: 5xx, server error, internal
  if (
    message.includes('500') ||
    message.includes('502') ||
    message.includes('503') ||
    message.includes('504') ||
    message.includes('internal server') ||
    message.includes('server error')
  ) {
    return 'server';
  }

  return 'unknown';
}

function errorToMessage(error: unknown): string {
  if (error == null) return '';
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && 'message' in error) {
    const msg = (error as { message?: unknown }).message;
    return typeof msg === 'string' ? msg : '';
  }
  return '';
}

const TONE_COPY: Record<ServiceErrorTone, {
  eyebrow: string;
  headline: string;
  body: (intent: ServiceErrorIntent, surface: string) => string;
  primaryLabel: string;
  Icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
}> = {
  auth: {
    eyebrow: 'Signed out',
    headline: 'Sign back in to continue.',
    body: () => 'Your session expired or signed out elsewhere. Sign back in and pick up where you were.',
    primaryLabel: 'Sign in',
    Icon: LogIn,
  },
  network: {
    eyebrow: 'Connection',
    headline: "Couldn't reach our servers.",
    body: () =>
      "Check your connection and try again. The parts of this surface you've already loaded will still work while you're offline.",
    primaryLabel: 'Try again',
    Icon: WifiOff,
  },
  server: {
    eyebrow: 'Try again',
    headline: 'Our side hiccupped.',
    body: () => 'A server-side issue stopped this load. Usually a few seconds is all it takes.',
    primaryLabel: 'Try again',
    Icon: RefreshCcw,
  },
  unknown: {
    eyebrow: 'Try again',
    headline: 'Something went wrong here.',
    body: (intent, surface) =>
      intent === 'submit'
        ? "That action didn't go through. Try once more — if it keeps failing, reach out via Ask The Lab."
        : `${surface || 'Something'} couldn't load. Refresh and try again — if it sticks, ping us in Ask The Lab.`,
    primaryLabel: 'Try again',
    Icon: AlertTriangle,
  },
};

export const ServiceErrorState = React.forwardRef<HTMLDivElement, ServiceErrorStateProps>(
  (
    {
      error,
      intent = 'load',
      surface,
      onRetry,
      onSignIn,
      toneOverride,
      compact = false,
      className,
      ...rest
    },
    ref,
  ) => {
    const tone = toneOverride ?? classifyServiceError(error);
    const copy = TONE_COPY[tone];
    const Icon = copy.Icon;

    // For auth errors, prefer onSignIn; fall back to onRetry; fall back to a refresh button.
    const primaryAction =
      tone === 'auth' && onSignIn
        ? { label: copy.primaryLabel, run: onSignIn }
        : onRetry
          ? { label: copy.primaryLabel, run: onRetry }
          : null;

    return (
      <div
        ref={ref}
        role="alert"
        className={cn(
          'mx-auto w-full max-w-[640px] rounded-[14px] border border-fd-coral/40 bg-fd-coral-soft',
          compact ? 'p-4' : 'p-6',
          className,
        )}
        {...rest}
      >
        <Eyebrow className="mb-2 inline-flex items-center gap-1.5">
          <Icon className="h-3 w-3" strokeWidth={2.25} />
          {copy.eyebrow}
        </Eyebrow>
        <p
          className={cn(
            'font-geist font-medium leading-[1.25] tracking-[-0.01em] text-fd-text',
            compact ? 'text-[14px]' : 'text-[16px]',
          )}
        >
          {copy.headline}
        </p>
        {!compact && (
          <p className="mt-2 max-w-[480px] text-[13px] leading-[1.55] text-fd-text-muted">
            {copy.body(intent, surface ?? '')}
          </p>
        )}

        {primaryAction && (
          <button
            type="button"
            onClick={primaryAction.run}
            className={cn(
              'mt-4 inline-flex h-9 items-center gap-1.5 rounded-[5px] border-[1.5px] border-fd-text bg-fd-paper px-3 font-geist-mono text-[11.5px] font-bold uppercase tracking-[0.05em] text-fd-text transition-colors hover:bg-fd-text hover:text-fd-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fd-text',
            )}
          >
            <RefreshCcw className="h-3 w-3" strokeWidth={2.25} />
            {primaryAction.label}
          </button>
        )}
      </div>
    );
  },
);
ServiceErrorState.displayName = 'ServiceErrorState';
